import React, { useMemo } from 'react'
import Decimal from 'decimal.js'
import moment, { unitOfTime } from 'moment'
import lang from '@/lang'
import { makeStyles, useTheme } from '@mui/styles'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconTrashcan from '@assets/images/icons/trashcan.svg'
import MainIcon from '@assets/images/product/main_blue.svg'
import DocumentIcon from '@assets/images/product/document_blue.svg'
import StaffIcon from '@assets/images/product/staff_blue.svg'
import OrgamaxIcon from '@assets/images/product/orgamax_blue.svg'
import { SquareIconButton } from '@/modules/base/components/buttons'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { ModuleWithCount, SubscriptionWithCount } from '@/modules/moduleSubscription/types/module'
import { ChangeShoppingCartItem } from '@/modules/moduleSubscription/types/ShoppingCart'
import ThemeInterface from '@/theme-interface'
import { ProductTypes, dateConstants } from '@/modules/base/util/constants'
import { SelectInput } from '@/modules/base/components/inputs'
import calculatePrice from '@/modules/moduleSubscription/components/_helpers/calculatePrice'
import getDiscountSplit from '@/modules/moduleSubscription/components/_helpers/getDiscountSplit'
import { isNumber } from 'lodash'
import { ModuleIconMap } from '@/modules/moduleSubscription/components/list/module-item/module-item'
import { isMeinBuero } from '@/utils/build-env'

interface PriceRow {
  id: string
  pricePerMonth: number
  months: number
  total: number
}

interface ShoppingCartItemProps {
  item: {
    [key: string]: ModuleWithCount | SubscriptionWithCount;
  };
  type: string;
  onChange: ChangeShoppingCartItem;
  onDelete: (type: string, itemName: string) => void;
  readOnly: boolean;
  disabled?: boolean;
  selectedPeriod: string;
}

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  typeHint: {
    fontSize: theme.fontSizes.tiny,
  },
  titlePriceWrapper: {
    paddingBottom: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleWrapper: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  selectInput: {
    width: 70,
  },
  shoppingCartPriceWrapper: {
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
  },
  priceRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  shoppingCartPriceSelection: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  subscriptionTypeItem: {
    flexDirection: 'column',
    display: 'flex',
    background: theme.backgroundColor,
    borderRadius: 4,
    padding: '20px 20px 0 20px',
  },
  moduleItems: {
    flexDirection: 'column',
    display: 'flex',
    background: theme.backgroundColor,
    borderRadius: 4,
    padding: '0 20px 20px 20px',
  },
  subscriptionTypeIcon: {
    height: 12,
    width: 'auto',
  },
  planTitle: {
    fontWeight: theme.fontWeights.medium,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.borderColor,
    margin: '15px 0',
  },
  moduleHeader: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginBottom: 10,

    '& span': {
      fontWeight: theme.fontWeights.medium,
    },
  },
  deleteButton: {
    marginLeft: 'auto',
  },
}))

const isMB = isMeinBuero()

// orgaMAX only sells one product in the shop, so it shows the plain brand logo instead of a product logo
const subscriptionTypeIcon = {
  [ProductTypes.MAIN]: MainIcon,
  [ProductTypes.DOCUMENT]: DocumentIcon,
  [ProductTypes.STAFF]: StaffIcon,
}

const ShoppingCartItem: React.FC<ShoppingCartItemProps> = (props) => {
  const {
    item = {},
    type = '',
    onChange = () => {},
    onDelete = () => {},
    readOnly = false,
    disabled = false,
    selectedPeriod,
  } = props
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()

  const subscriptionTypeItem = Object.entries(item).find(([_, value]) => value.type === type)?.[1] as SubscriptionWithCount
  const moduleItems = Object.entries(item).filter(([_, value]) => value.type === undefined).map(([_, value]) => value as ModuleWithCount) || []

  const priceValues = subscriptionTypeItem?.prices?.find((price) => price.period === selectedPeriod)?.values?.[0]
  const { pricePerDuration = 0, duration = 1 } = priceValues || {}
  const divideFactorForMonth = pricePerDuration && duration
    ? Math.round(moment().add(duration, selectedPeriod as unitOfTime.DurationConstructor).diff(moment(), 'months', true))
    : 1

  const subscriptionTypeItemEl = useMemo(() => {
    if (!subscriptionTypeItem) return null

    const price = calculatePrice(priceValues, subscriptionTypeItem.type, selectedPeriod)
    const hasPrice = isNumber(price) || price instanceof Decimal
    const priceEl = hasPrice
      ? formatCurrency(new Decimal(price).dividedBy(divideFactorForMonth).toDP(2).toNumber())
      : lang.moduleSubscription.list.shoppingCart.item.priceNotExists

    // a discount that only covers part of the term is listed as one row per price, so the customer
    // sees the actual monthly amounts instead of their average. Both shapes share one structure, so
    // the quantity cannot end up applied to only some of the rows.
    const { count } = subscriptionTypeItem
    const discountSplit = hasPrice ? getDiscountSplit(priceValues, selectedPeriod, subscriptionTypeItem.type) : null
    let priceRows: PriceRow[] = []
    if (discountSplit) {
      priceRows = [
        { id: 'discounted', pricePerMonth: discountSplit.discountedPricePerMonth, months: discountSplit.discountedMonths },
        { id: 'regular', pricePerMonth: discountSplit.regularPricePerMonth, months: discountSplit.regularMonths },
      ].map(row => ({
        ...row,
        total: new Decimal(row.pricePerMonth).times(count).times(row.months).toDP(2).toNumber(),
      }))
    } else if (hasPrice) {
      // a single row keeps the exact interval price, rather than rounding it via the month price
      priceRows = [{
        id: 'single',
        pricePerMonth: new Decimal(price).dividedBy(divideFactorForMonth).toDP(2).toNumber(),
        months: selectedPeriod === dateConstants.YEAR ? 12 : 1,
        total: new Decimal(price).times(count).toDP(2).toNumber(),
      }]
    }

    // initial array for module select options, default is 1
    const itemSelectOptions = [{ name: '1' }]
    // if the module is bookable more than once, the select options array is expanded to 10
    if (subscriptionTypeItem.bookable === 0) {
      for (let i = 2; i <= 10; i++) {
        itemSelectOptions.push({ name: i.toString() })
      }
    }

    return (
      <div className={classes.subscriptionTypeItem} style={{ paddingBottom: !moduleItems?.length ? '20px' : 0 }}>
        <div className={classes.titlePriceWrapper}>
          <div className={classes.titleWrapper}>
            {ModuleIconMap[subscriptionTypeItem.name] && (
              <SVGIcon
                svg={ModuleIconMap[subscriptionTypeItem.name]}
                height='30px'
                width='30px'
              />
            )}
            <div>
              <div className={classes.typeHint}><SVGIcon className={classes.subscriptionTypeIcon} svg={isMB ? subscriptionTypeIcon[type] : OrgamaxIcon} height={30} /></div>
              <div className={classes.planTitle}>{`${subscriptionTypeItem.title}${subscriptionTypeItem.amount > 1 ? ` (x${subscriptionTypeItem.amount})` : ''}`}</div>
            </div>
          </div>
          {(!readOnly && !subscriptionTypeItem.isProductItem)
            && <SquareIconButton
              tooltip={lang.generics.delete}
              icon={IconTrashcan}
              iconColor={theme.black}
              variant='grey'
              size='medium'
              dataQsId={`delete-shopping-cart-item-${subscriptionTypeItem.name}`}
              onClick={() => onDelete(type, subscriptionTypeItem.name)}
              disabled={disabled}
            />}
        </div>
        {(ModuleIconMap[subscriptionTypeItem.name] && !readOnly && price)
          ? <div className={classes.shoppingCartPriceWrapper}>
            <div className={classes.shoppingCartPriceSelection}>
              <SelectInput
                className={classes.selectInput}
                allowCreate={false}
                notAsync
                customSelectOptions={{
                  menuPosition: 'fixed',
                  isSearchable: false,
                  valueKey: 'name',
                  labelKey: 'name',
                  isClearable: false,
                }}
                value={{ name: subscriptionTypeItem.count.toString() }}
                options={itemSelectOptions}
                handleChange={(value) => onChange(type, subscriptionTypeItem, parseInt(value.name, 10), true)}
              />
              <div> x {priceEl}</div>
            </div>
            <div>{formatCurrency(new Decimal(price).dividedBy(divideFactorForMonth).times(subscriptionTypeItem.count).toDP(2).toNumber())}</div>
          </div>
          : <div className={classes.priceRows}>
            {priceRows.length
              ? priceRows.map(({ id, pricePerMonth, months, total }) => (
                <div className={classes.shoppingCartPriceWrapper} key={id} data-qs-id={`shopping-cart-price-row-${id}`}>
                  <div>
                    {formatCurrency(new Decimal(pricePerMonth).times(count).toDP(2).toNumber())}
                    {' '}x{' '}{lang.moduleSubscription.list.shoppingCart.periodInMonths(months)}
                  </div>
                  <div>{formatCurrency(total)}</div>
                </div>
              ))
              : <div className={classes.shoppingCartPriceWrapper}>{priceEl}</div>}
          </div>
        }
      </div>
    )
  }, [subscriptionTypeItem, moduleItems, readOnly, disabled, onChange, onDelete, type, selectedPeriod, divideFactorForMonth])

  return (
    <div className={classes.shoppingCartItem}>
      {subscriptionTypeItemEl}
      <div>
        {moduleItems.length > 0 && (
          <div className={classes.moduleItems} style={{ paddingTop: !subscriptionTypeItemEl ? '20px' : 0 }}>
            {moduleItems.map((moduleItem, index) => {
              const price = calculatePrice(moduleItem.prices?.find((price) => price.period === selectedPeriod)?.values?.[0], moduleItem.type, selectedPeriod)
              return (
                <div key={moduleItem.name}>
                  {(subscriptionTypeItemEl || index > 0) && <div className={classes.divider} />}
                  <div className={classes.moduleHeader}>
                    <SVGIcon
                      svg={ModuleIconMap[moduleItem.name]}
                      height='24px'
                      width='24px'
                    />
                    <span>{moduleItem.title}</span>
                    {!readOnly
                      && <SquareIconButton
                        tooltip={lang.generics.delete}
                        icon={IconTrashcan}
                        iconColor={theme.black}
                        variant='grey'
                        size='medium'
                        onClick={() => onDelete(type, moduleItem.name)}
                        disabled={disabled}
                        buttonWrapperClass={classes.deleteButton}
                      />}
                  </div>
                  <div className={classes.shoppingCartPriceWrapper}>
                    <div>
                      {moduleItem.count > 1 && `${moduleItem.count} x `}
                      {formatCurrency(new Decimal(price).dividedBy(divideFactorForMonth).toDP(2).toNumber())} x {
                        selectedPeriod === dateConstants.YEAR
                          ? lang.moduleSubscription.list.shoppingCart.item.twelveMonths
                          : lang.moduleSubscription.list.shoppingCart.item.oneMonth
                      }
                    </div>
                    <div>
                      {formatCurrency(new Decimal(price).times(moduleItem.count).toDP(2).toNumber())}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ShoppingCartItem
